import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../../../const/axios";

export interface DiscussionComment {
  id: number;
  text: string;
  added: string;
  user?: {
    id?: number;
    name?: string;
    surname?: string;
    photo?: string | null;
  };
}

export interface DiscussionEntry {
  average: number;
  count: number;
  myRating: number | null;
  commentCount: number;
  /** Null until the item's comments panel is first opened. */
  comments: DiscussionComment[] | null;
}

export type DiscussionEntityType = "question" | "theme";

interface DiscussionContextValue {
  entityType: DiscussionEntityType;
  entries: Record<number, DiscussionEntry>;
  loadComments: (id: number) => Promise<void>;
  setEntry: (id: number, patch: Partial<DiscussionEntry>) => void;
}

const DiscussionContext = createContext<DiscussionContextValue | null>(null);

const collectionPath = (entityType: DiscussionEntityType) =>
  entityType === "question" ? "questions" : "themes";

const emptyEntry: DiscussionEntry = { average: 0, count: 0, myRating: null, commentCount: 0, comments: null };

const normalizeRow = (row: any): DiscussionEntry => ({
  average: row?.average ?? 0,
  count: row?.count ?? 0,
  myRating: row?.myRating ?? null,
  commentCount: row?.commentCount ?? 0,
  comments: null,
});

interface DiscussionProviderProps {
  entityType: DiscussionEntityType;
  ids: number[];
  children: React.ReactNode;
}

export const DiscussionProvider: React.FC<DiscussionProviderProps> = ({ entityType, ids, children }) => {
  const [entries, setEntries] = useState<Record<number, DiscussionEntry>>({});

  const idKey = useMemo(() => ids.slice().sort((a, b) => a - b).join(","), [ids]);

  useEffect(() => {
    if (!idKey) {
      setEntries({});
      return;
    }
    let cancelled = false;
    const path = collectionPath(entityType);
    api.get(`${path}/discussion`, { params: { ids: idKey } })
      .then((res) => {
        if (cancelled) return;
        const list: Array<{ id: number } & Record<string, any>> = res.data ?? [];
        setEntries((prev) => {
          const map: Record<number, DiscussionEntry> = {};
          list.forEach((row) => {
            // A comments panel opened before this summary arrived keeps what it loaded.
            map[row.id] = { ...normalizeRow(row), comments: prev[row.id]?.comments ?? null };
          });
          return map;
        });
      })
      .catch(() => {
        if (cancelled) return;
        setEntries({});
      });

    return () => { cancelled = true; };
  }, [entityType, idKey]);

  const setEntry = useCallback((id: number, patch: Partial<DiscussionEntry>) => {
    setEntries((prev) => ({ ...prev, [id]: { ...emptyEntry, ...prev[id], ...patch } }));
  }, []);

  // Comment bodies are only fetched for the item whose comments panel is opened; the batch
  // summary above carries just ratings and counts so it stays cheap for a whole phase.
  const loadComments = useCallback(async (id: number) => {
    const path = collectionPath(entityType);
    try {
      const res = await api.get(`${path}/${id}/comments`);
      const comments: DiscussionComment[] = res.data ?? [];
      setEntry(id, { comments, commentCount: comments.length });
    } catch {
      // keep whatever was shown before
    }
  }, [entityType, setEntry]);

  const value = useMemo<DiscussionContextValue>(() => ({
    entityType,
    entries,
    loadComments,
    setEntry,
  }), [entityType, entries, loadComments, setEntry]);

  return <DiscussionContext.Provider value={value}>{children}</DiscussionContext.Provider>;
};

export const useDiscussion = (id: number) => {
  const ctx = useContext(DiscussionContext);
  const ctxLoad = ctx?.loadComments;
  const ctxSet = ctx?.setEntry;
  const loadComments = useCallback(() => (ctxLoad ? ctxLoad(id) : Promise.resolve()), [ctxLoad, id]);
  const setEntry = useCallback((patch: Partial<DiscussionEntry>) => ctxSet?.(id, patch), [ctxSet, id]);
  return {
    available: ctx !== null,
    entityType: ctx?.entityType ?? ("question" as DiscussionEntityType),
    entry: ctx?.entries[id],
    loadComments,
    setEntry,
  };
};
